import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import { HpiDataTable, HpiDateFilterBar, HpiStatsBar } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import {
  caseExpensePath,
  caseTimeEntryPath,
  teamPayrollDetailPath,
  teamPayrollPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface PayPeriodRow {
  week1: number
  week2: number | null
  week1_start: string
  week2_end: string
  otj_minutes: number
  travel_minutes: number
  regular_minutes: number
  overtime_minutes: number
  pto_hours: number
  sto_hours: number
}

interface PendingEntry {
  id: number
  activity_date: string | null
  case_id: number
  case_name: string | null
  activity_type: "Time" | "Expense"
  sub_type: string | null
  description: string | null
  added_at: string | null
}

interface Props {
  employee: { id: number; name: string }
  pay_periods: PayPeriodRow[]
  pending_entries: PendingEntry[]
  start_date: string
  end_date: string
}

/* ── Helpers ────────────────────────────────────────────── */

function formatMinutes(totalMin: number): string {
  if (totalMin <= 0) return ""
  const hours = Math.floor(totalMin / 60)
  const mins = totalMin % 60
  return `${hours}:${mins.toString().padStart(2, "0")}`
}

function formatDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })
}

/* ── Constants ──────────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Team", href: "/team" },
  { title: "Payroll History", href: teamPayrollPath() },
]

const payPeriodColumns: HpiColumn<PayPeriodRow>[] = [
  {
    header: "Wk Range",
    width: "w-[220px] !flex-none",
    render: (row) => {
      const weekLabel = row.week2 ? `${row.week1}-${row.week2}` : `${row.week1}`
      return (
        <span className="text-sm">
          <span className="font-semibold">{weekLabel}</span>{" "}
          {formatDate(row.week1_start)}-{formatDate(row.week2_end)}
        </span>
      )
    },
  },
  {
    header: "OTJ",
    align: "right",
    render: (row) => (
      <span className="text-sm">{formatMinutes(row.otj_minutes)}</span>
    ),
  },
  {
    header: "Travel",
    align: "right",
    render: (row) => (
      <span className="text-sm">{formatMinutes(row.travel_minutes)}</span>
    ),
  },
  {
    header: "Reg",
    align: "right",
    render: (row) => (
      <span className="text-sm">{formatMinutes(row.regular_minutes)}</span>
    ),
  },
  {
    header: "OT",
    align: "right",
    render: (row) => (
      <span className="text-sm">{formatMinutes(row.overtime_minutes)}</span>
    ),
  },
  {
    header: "Hours",
    align: "right",
    render: (row) => (
      <span className="text-sm font-medium">
        {formatMinutes(row.otj_minutes + row.travel_minutes)}
      </span>
    ),
  },
]

const pendingColumns: HpiColumn<PendingEntry>[] = [
  {
    header: "Date",
    width: "w-[100px] !flex-none",
    render: (row) => (
      <span className="text-sm">{formatDate(row.activity_date)}</span>
    ),
  },
  {
    header: "Case",
    render: (row) => <span className="text-sm">{row.case_name ?? ""}</span>,
  },
  {
    header: "Type",
    render: (row) => (
      <span className="text-sm">
        {row.activity_type}
        {row.sub_type ? `/${row.sub_type}` : ""}
      </span>
    ),
  },
  {
    header: "Description",
    render: (row) => <span className="text-sm">{row.description ?? ""}</span>,
  },
  {
    header: "Added",
    width: "w-[100px] !flex-none",
    render: (row) => (
      <span className="text-sm">{formatDate(row.added_at)}</span>
    ),
  },
]

/* ── Component ──────────────────────────────────────────── */

export default function Payroll({
  employee,
  pay_periods,
  pending_entries,
  start_date,
  end_date,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)

  const handleUpdate = () => {
    const params: Record<string, string> = {}
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate
    router.get(teamPayrollPath(), params, { preserveState: true })
  }

  // Calculate totals
  const totals = pay_periods.reduce(
    (acc, pp) => ({
      otj_minutes: acc.otj_minutes + pp.otj_minutes,
      travel_minutes: acc.travel_minutes + pp.travel_minutes,
      regular_minutes: acc.regular_minutes + pp.regular_minutes,
      overtime_minutes: acc.overtime_minutes + pp.overtime_minutes,
    }),
    {
      otj_minutes: 0,
      travel_minutes: 0,
      regular_minutes: 0,
      overtime_minutes: 0,
    },
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Payroll History" />

      <div className="p-6">
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        />

        {pending_entries.length > 0 && (
          <div className="mb-6">
            <HpiDataTable<PendingEntry>
              title="Pending Entries"
              columns={pendingColumns}
              data={pending_entries}
              keyFn={(row) => `${row.activity_type}-${row.id}`}
              rowHref={(row) => {
                if (!row.case_id) return undefined
                return row.activity_type === "Time"
                  ? caseTimeEntryPath(row.case_id, row.id)
                  : caseExpensePath(row.case_id, row.id)
              }}
              rowTarget="_blank"
            />
          </div>
        )}

        {pay_periods.length > 0 ? (
          <>
            <HpiDataTable<PayPeriodRow>
              title={`Payroll History - ${employee.name}`}
              columns={payPeriodColumns}
              data={pay_periods}
              keyFn={(row) => `${row.week1}-${row.week2 ?? "solo"}`}
              rowHref={(row) =>
                teamPayrollDetailPath({
                  week_start: row.week1_start,
                  return_start_date: startDate,
                  return_end_date: endDate,
                })
              }
              rowTarget="_blank"
              pagination={
                <div className="flex items-start gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold">
                  <div className="w-[220px] flex-none">Total</div>
                  <div className="min-w-0 flex-1 text-right">
                    {formatMinutes(totals.otj_minutes)}
                  </div>
                  <div className="min-w-0 flex-1 text-right">
                    {formatMinutes(totals.travel_minutes)}
                  </div>
                  <div className="min-w-0 flex-1 text-right">
                    {formatMinutes(totals.regular_minutes)}
                  </div>
                  <div className="min-w-0 flex-1 text-right">
                    {formatMinutes(totals.overtime_minutes)}
                  </div>
                  <div className="min-w-0 flex-1 text-right">
                    {formatMinutes(totals.otj_minutes + totals.travel_minutes)}
                  </div>
                </div>
              }
            />

            <HpiStatsBar
              className="mt-6"
              stats={[
                {
                  label: "Total Hours",
                  value: formatMinutes(
                    totals.otj_minutes + totals.travel_minutes,
                  ),
                },
                {
                  label: "OTJ",
                  value: formatMinutes(totals.otj_minutes),
                },
                {
                  label: "Travel",
                  value: formatMinutes(totals.travel_minutes),
                },
                {
                  label: "Overtime",
                  value: formatMinutes(totals.overtime_minutes),
                },
              ]}
            />
          </>
        ) : (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            No time/expenses found between {formatDate(start_date)} and{" "}
            {formatDate(end_date)}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
